import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faTrash, faPlus } from '@fortawesome/free-solid-svg-icons';
import MenuService from '../services/menuService';
import './NCItemsPage.css';

const NCItemsPage = ({ ncItems, onAddItem, onRemoveItem, onBack, onConfirm, onUpdateItemNotes }) => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [ncForm, setNcForm] = useState({
        itemName: '',
        quantity: 1,
        price: ''
    });
    const [menuList, setMenuList] = useState([]);
    const [ncSuggestions, setNcSuggestions] = useState([]);
    const [showNcSuggestions, setShowNcSuggestions] = useState(false);
    const [selectedNcMenuItemId, setSelectedNcMenuItemId] = useState(null);
    const [ncModalError, setNcModalError] = useState('');

    useEffect(() => {
        loadMenuItems();
    }, []);

    const loadMenuItems = async () => {
        try {
            const resp = await MenuService.getMenuItems('en', true);
            if (resp && resp.success && resp.data && Array.isArray(resp.data)) {
                setMenuList(resp.data);
            }
        } catch (e) {
            console.error('Failed to load menu items:', e);
        }
    };

    const handleNcItemNameChange = (e) => {
        const searchValue = e.target.value;
        console.log('⌨️ Input changed:', {
            newValue: searchValue,
            currentFormValue: ncForm.itemName,
            selectedMenuItemId: selectedNcMenuItemId
        });
        
        // If user modifies text after selecting, clear the selection
        if (selectedNcMenuItemId && searchValue !== ncForm.itemName) {
            console.log('❌ Clearing selection - text modified after selection');
            setSelectedNcMenuItemId(null);
            setNcForm({ itemName: searchValue, quantity: ncForm.quantity, price: '' });
            setNcModalError('');
            
            // Show suggestions again since user is changing the text
            if (searchValue.trim()) {
                const filtered = menuList
                    .filter(item => item.name?.toLowerCase().includes(searchValue.toLowerCase()))
                    .slice(0, 10);
                setNcSuggestions(filtered);
                setShowNcSuggestions(true);
            } else {
                setNcSuggestions([]);
                setShowNcSuggestions(false);
            }
        } else if (!selectedNcMenuItemId) {
            // Only show suggestions if nothing is selected yet
            console.log('🔍 Searching for suggestions (no item selected yet)');
            setNcForm({ ...ncForm, itemName: searchValue });
            
            if (searchValue.trim()) {
                const filtered = menuList
                    .filter(item => item.name?.toLowerCase().includes(searchValue.toLowerCase()))
                    .slice(0, 10);
                setNcSuggestions(filtered);
                setShowNcSuggestions(true);
            } else {
                setNcSuggestions([]);
                setShowNcSuggestions(false);
            }
        } else {
            console.log('✅ Item already selected, text unchanged - keeping selection');
        }
    };

    const handleNcItemSelect = (item) => {
        console.log('🎯 NC Item Selected:', {
            id: item.id,
            name: item.name,
            price: item.price
        });
        setNcForm({
            itemName: item.name,
            quantity: ncForm.quantity || 1,
            price: item.price || 0
        });
        setSelectedNcMenuItemId(item.id);
        setShowNcSuggestions(false);
        setNcSuggestions([]);
        setNcModalError('');
        console.log('✅ Selection saved. Menu Item ID:', item.id);
    };

    const handleNcSubmit = () => {
        console.log('📤 Submit clicked! Current state:', {
            formValues: ncForm,
            selectedMenuItemId: selectedNcMenuItemId,
            modalError: ncModalError
        });
        
        if (!ncForm.itemName.trim()) {
            console.log('❌ Validation failed: No item name');
            setNcModalError('Please select a menu item');
            return;
        }
        if (!selectedNcMenuItemId) {
            console.log('❌ Validation failed: No menu item ID selected');
            setNcModalError('Please select a valid menu item from the suggestions');
            return;
        }
        if (!ncForm.quantity || ncForm.quantity < 1) {
            console.log('❌ Validation failed: Invalid quantity');
            setNcModalError('Quantity must be at least 1');
            return;
        }

        const newNcItem = {
            menuItemId: selectedNcMenuItemId,
            itemName: ncForm.itemName,
            quantity: parseInt(ncForm.quantity),
            price: parseFloat(ncForm.price) || 0
        };

        console.log('✅ Validation passed! Adding NC item:', newNcItem);
        onAddItem(newNcItem);
        
        // Reset form
        setNcForm({ itemName: '', quantity: 1, price: '' });
        setSelectedNcMenuItemId(null);
        setNcModalError('');
        setShowAddModal(false);
    };

    const getTotalAmount = () => {
        return ncItems.reduce((sum, item) => {
            return sum + (item.quantity * (item.price || 0));
        }, 0).toFixed(2);
    };

    return (
        <div className="nc-items-page">
            {/* Header */}
            <div className="nc-page-header">
                <button className="nc-back-btn" onClick={onBack}>
                    <FontAwesomeIcon icon={faArrowLeft} className="nc-back-icon" />
                </button>
                <h2 className="nc-page-title">NC Items (Non Chargeable)</h2>
            </div>

            {/* Centered Container */}
            <div className="nc-centered-container">
                {/* NC Items List */}
                <div className="nc-items-container">
                {ncItems.length === 0 ? (
                    <div className="nc-empty-state">
                        <div className="nc-empty-icon">📋</div>
                        <p className="nc-empty-text">No NC items added yet</p>
                        <p className="nc-empty-subtext">Click the + button below to add items</p>
                    </div>
                ) : (
                    <>
                        {ncItems.map((item, index) => (
                            <div key={`${item.menuItemId}-${index}`} className="nc-item-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <div className="nc-item-content">
                                        <h3 className="nc-item-name">{item.itemName} X {item.quantity}</h3>
                                        <div className="nc-item-info">
                                            <span className="nc-item-price">₹{item.price || '0'}</span>
                                        </div>
                                    </div>
                                    <button
                                        className="nc-item-delete-btn"
                                        onClick={() => onRemoveItem(index)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                                <div className="nc-item-notes-wrapper" style={{ marginTop: '12px', width: '100%' }}>
                                    <input
                                        type="text"
                                        placeholder="Add note/description..."
                                        value={item.notes || ''}
                                        onChange={(e) => onUpdateItemNotes && onUpdateItemNotes(index, e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            border: '1px solid #e5e7eb',
                                            borderRadius: '8px',
                                            fontSize: '13px',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            backgroundColor: '#f9fafb',
                                            color: '#111827'
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                        
                        {/* Total Card */}
                        <div className="nc-total-card">
                            <span className="nc-total-label">Total Value</span>
                            <span className="nc-total-amount">₹{getTotalAmount()}</span>
                        </div>

                        {/* Confirm Button */}
                        <button className="nc-confirm-btn" onClick={() => onConfirm ? onConfirm() : onBack()}>
                            Confirm NC Items
                        </button>
                    </>
                )}
            </div>
            </div>

            {/* Add Button */}
            <button className="nc-add-fab" onClick={() => setShowAddModal(true)}>
                <FontAwesomeIcon icon={faPlus} />
            </button>

            {/* Add Modal */}
            {showAddModal && (
                <div className="nc-modal-overlay">
                    <div className="nc-modal-content">
                        <button 
                            className="nc-modal-close" 
                            onClick={() => setShowAddModal(false)}
                            aria-label="Close"
                        >
                            ×
                        </button>
                        <h3 className="nc-modal-title">Add NC Item (Non Chargeable)</h3>
                        
                        {ncModalError && (
                            <div className="nc-modal-error">
                                {ncModalError}
                            </div>
                        )}
                        
                        <div className="nc-form-group">
                            <label className="nc-form-label">Item Name</label>
                            <div className="nc-search-container">
                                <input
                                    type="text"
                                    className="nc-form-input"
                                    value={ncForm.itemName}
                                    onChange={handleNcItemNameChange}
                                    placeholder="Search for menu item..."
                                    autoFocus
                                />
                                {showNcSuggestions && ncSuggestions.length > 0 && (
                                    <div className="nc-suggestions">
                                        {ncSuggestions.map((item) => (
                                            <div
                                                key={item.id}
                                                className="nc-suggestion-item"
                                                onMouseDown={(e) => {
                                                    e.preventDefault();
                                                    handleNcItemSelect(item);
                                                }}
                                            >
                                                <div className="nc-suggestion-name">{item.name}</div>
                                                <div className="nc-suggestion-price">Price: ₹{item.price || 0}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        <div className="nc-form-row">
                            <div className="nc-form-group">
                                <label className="nc-form-label">Quantity</label>
                                <input
                                    type="number"
                                    className="nc-form-input"
                                    min="1"
                                    value={ncForm.quantity}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === '') {
                                            setNcForm({ ...ncForm, quantity: '' });
                                        } else {
                                            const numVal = parseInt(val);
                                            if (!isNaN(numVal) && numVal >= 1) {
                                                setNcForm({ ...ncForm, quantity: numVal });
                                            }
                                        }
                                    }}
                                    onBlur={(e) => {
                                        const val = parseInt(e.target.value);
                                        if (isNaN(val) || val < 1) {
                                            setNcForm({ ...ncForm, quantity: 1 });
                                        }
                                    }}
                                />
                            </div>
                            
                            <div className="nc-form-group">
                                <label className="nc-form-label">Price (per item)</label>
                                <input
                                    type="text"
                                    className="nc-form-input nc-form-input-readonly"
                                    value={ncForm.price ? `₹${ncForm.price}` : ''}
                                    readOnly
                                    placeholder="Select an item first"
                                />
                            </div>
                        </div>
                        
                        <div className="nc-modal-actions">
                            <button
                                className="nc-modal-btn nc-modal-btn-cancel"
                                onClick={() => {
                                    setShowAddModal(false);
                                    setNcForm({ itemName: '', quantity: 1, price: '' });
                                    setNcModalError('');
                                    setNcSuggestions([]);
                                    setShowNcSuggestions(false);
                                    setSelectedNcMenuItemId(null);
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                className="nc-modal-btn nc-modal-btn-submit"
                                onClick={handleNcSubmit}
                            >
                                Add NC Item
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NCItemsPage;
